import React from 'react';
import { FinancialValue } from './shared/FinancialValue';
import { formatCurrency } from '../utils/financialFormat';

interface FinancialData {
  revenue_2019: number;
  revenue_2020: number;
  revenue_2021: number;
  revenue_2022: number;
  variable_profit_2019?: number;
  variable_profit_2020?: number;
  variable_profit_2021?: number;
  variable_profit_2022?: number;
  monthly_data?: Array<{
    month: string;
    revenue: number;
    variable_profit: number;
  }>;
}

interface Props {
  data: FinancialData;
}

export default function FinancialSummary({ data }: Props) {
  // Variable Profit is produced by the deterministic engine (variable_profit_YYYY).
  // We RENDER that value; when absent we show "—" — never approximate VP from a
  // revenue * margin ratio (that would fabricate a financial figure).
  const revenueData = [
    { year: '2019', amount: data.revenue_2019 || 0, vp: data.variable_profit_2019 ?? null },
    { year: '2020', amount: data.revenue_2020 || 0, vp: data.variable_profit_2020 ?? null },
    { year: '2021', amount: data.revenue_2021 || 0, vp: data.variable_profit_2021 ?? null },
    { year: '2022', amount: data.revenue_2022 || 0, vp: data.variable_profit_2022 ?? null }
  ];

  const maxRevenue = Math.max(...revenueData.map(d => d.amount));
  const scale = maxRevenue > 0 ? 100 / maxRevenue : 1;

  return (
    <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>4-Year Financial Summary</h3>

      {/* Revenue Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2" style={{ borderColor: 'var(--color-border)' }}>
              <th className="text-left py-2 px-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Year</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Revenue</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Variable Profit</th>
              <th className="text-right py-2 px-3 font-semibold" style={{ color: 'var(--color-text-secondary)' }}>VP %</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.map((row, idx) => {
              const hasVP = row.vp !== null && row.amount > 0;
              const vpPercent = hasVP ? ((row.vp! / row.amount) * 100).toFixed(1) : null;
              const isDown = idx > 0 && row.amount < revenueData[idx - 1].amount;
              return (
                <tr key={row.year} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                  <td className="py-2 px-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>{row.year}</td>
                  <td className="text-right py-2 px-3 font-semibold" style={{ color: isDown ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}>
                    <FinancialValue value={row.amount} />
                  </td>
                  <td className="text-right py-2 px-3" style={{ color: 'var(--color-text-secondary)' }}>
                    <FinancialValue value={row.vp} />
                  </td>
                  <td className="text-right py-2 px-3" style={{ color: 'var(--color-text-muted)' }}>
                    {vpPercent !== null ? `${vpPercent}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
          Variable Profit is computed by the deterministic engine. "—" means the engine value is not available — it is never approximated from a margin.
        </p>
      </div>

      {/* Visual Chart */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>Revenue vs Variable Profit Trend</h4>
        {revenueData.map((row) => (
          <div key={row.year} className="space-y-1">
            <div className="flex justify-between text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>
              <span>{row.year}</span>
              <span><FinancialValue value={row.amount} /></span>
            </div>
            <div className="relative h-8 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              {/* Revenue bar */}
              <div
                className="absolute top-0 left-0 h-4 rounded"
                style={{ width: `${row.amount * scale}%`, backgroundColor: 'var(--color-brand)' }}
                title={`Revenue: ${formatCurrency(row.amount)}`}
              />
              {/* Variable Profit bar — only when the engine provided VP */}
              {row.vp !== null && (
                <div
                  className="absolute bottom-0 left-0 h-4 rounded"
                  style={{ width: `${row.vp * scale}%`, backgroundColor: 'var(--color-verified)' }}
                  title={`Variable Profit: ${formatCurrency(row.vp)}`}
                />
              )}
            </div>
          </div>
        ))}
        <div className="flex gap-4 text-xs mt-3">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'var(--color-brand)' }}></div>
            <span style={{ color: 'var(--color-text-muted)' }}>Revenue</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'var(--color-verified)' }}></div>
            <span style={{ color: 'var(--color-text-muted)' }}>Variable Profit</span>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>Key Insights</h4>
        <ul className="space-y-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          <li>• 2020 Revenue Impact: <span className="font-semibold" style={{ color: 'var(--color-text-muted)' }}><FinancialValue value={(data.revenue_2020 || 0) - (data.revenue_2019 || 0)} /></span> (-{((((data.revenue_2019 || 0) - (data.revenue_2020 || 0)) / (data.revenue_2019 || 1)) * 100).toFixed(1)}%)</li>
          <li>• Recovery by 2021: <FinancialValue value={data.revenue_2021 || 0} /> ({(((data.revenue_2021 || 0) / (data.revenue_2019 || 1)) * 100).toFixed(1)}% of 2019)</li>
          <li>• 2022 Status: {(data.revenue_2022 || 0) > (data.revenue_2019 || 0) ? 'Exceeded' : 'Below'} pre-pandemic levels</li>
        </ul>
      </div>
    </div>
  );
}
